import { useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import { Share2, Loader2, Download, Copy, Mail, Link2, CheckCircle2 } from "lucide-react";
import { printInventorySnapshot } from "@/lib/inventoryPdf";

// One dialog, three actions: Print PDF • Copy shareable link • Email a supplier.
// The public share URL is created lazily when the user opens the "Link" tab.

export default function InventoryShareDialog({ open, onOpenChange, lowStockCount, itemCount }) {
  const [tab, setTab] = useState("pdf");

  // Link state
  const [linkLoading, setLinkLoading] = useState(false);
  const [shareUrl, setShareUrl] = useState("");
  const [copied, setCopied] = useState(false);

  // Email supplier state
  const [supplierEmail, setSupplierEmail] = useState("");
  const [supplierName, setSupplierName] = useState("");
  const [notes, setNotes] = useState("");
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const printPdf = async () => {
    setLinkLoading(true);
    try {
      // Build a fresh snapshot so the print reflects current pantry state.
      const { data } = await api.post("/inventory/share", { include_full_list: true });
      printInventorySnapshot(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setLinkLoading(false);
    }
  };

  const createLink = async () => {
    setLinkLoading(true);
    try {
      const { data } = await api.post("/inventory/share", { include_full_list: true });
      setShareUrl(data.url);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setLinkLoading(false);
    }
  };

  const copyLink = async () => {
    if (!shareUrl) return;
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      toast.success("Link copied.");
      setTimeout(() => setCopied(false), 2500);
    } catch {
      toast.error("Copy failed: copy manually.");
    }
  };

  const emailSupplier = async () => {
    if (!supplierEmail) return toast.error("Add the supplier's email.");
    setSending(true);
    try {
      const { data } = await api.post("/inventory/email-supplier", {
        supplier_email: supplierEmail,
        supplier_name: supplierName || undefined,
        notes: notes || undefined,
      });
      if (data.sent) {
        toast.success(`Order request sent to ${supplierEmail} • ${data.item_count} item${data.item_count === 1 ? "" : "s"}.`);
        setSent(true);
      } else {
        toast.error("Email service not configured. Ask an admin to set SENDGRID_API_KEY.");
      }
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setSending(false);
    }
  };

  const close = () => {
    onOpenChange(false);
    setTimeout(() => {
      setShareUrl("");
      setCopied(false);
      setSent(false);
      setSupplierEmail("");
      setSupplierName("");
      setNotes("");
      setTab("pdf");
    }, 200);
  };

  return (
    <Dialog open={open} onOpenChange={(v) => v ? onOpenChange(true) : close()}>
      <DialogContent className="max-w-lg" data-testid="inventory-share-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl flex items-center gap-2">
            <Share2 className="w-5 h-5" /> Share inventory snapshot
          </DialogTitle>
        </DialogHeader>

        <div className="text-xs text-muted-foreground -mt-1 mb-4">
          {itemCount} priced items • <span className={lowStockCount > 0 ? "text-destructive font-medium" : "text-muted-foreground"}>{lowStockCount} below minimum stock</span>
        </div>

        <Tabs value={tab} onValueChange={setTab}>
          <TabsList className="w-full grid grid-cols-3">
            <TabsTrigger value="pdf" data-testid="share-tab-pdf" className="gap-1.5"><Download className="w-3.5 h-3.5" /> PDF</TabsTrigger>
            <TabsTrigger value="link" data-testid="share-tab-link" className="gap-1.5"><Link2 className="w-3.5 h-3.5" /> Link</TabsTrigger>
            <TabsTrigger value="email" data-testid="share-tab-email" className="gap-1.5"><Mail className="w-3.5 h-3.5" /> Supplier</TabsTrigger>
          </TabsList>

          <TabsContent value="pdf" className="pt-4 space-y-4">
            <p className="text-sm text-muted-foreground">
              A branded, print ready inventory snapshot: total value, every low stock item, full priced list. Suitable for handover, insurance filings, and the owner's binder.
            </p>
            <Button onClick={printPdf} disabled={linkLoading} className="w-full gap-2" data-testid="share-print">
              {linkLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
              Open printable snapshot
            </Button>
          </TabsContent>

          <TabsContent value="link" className="pt-4 space-y-3">
            <p className="text-sm text-muted-foreground">
              A public, view-only URL of this snapshot. Frozen at capture, so the numbers won't drift.
            </p>
            {!shareUrl ? (
              <Button onClick={createLink} disabled={linkLoading} className="w-full gap-2" data-testid="share-create-link">
                {linkLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Link2 className="w-4 h-4" />}
                Generate share link
              </Button>
            ) : (
              <div className="rounded-lg border border-border bg-secondary/40 p-3 space-y-2">
                <div className="text-xs text-muted-foreground">Share link</div>
                <div className="flex items-center gap-2">
                  <Input readOnly value={shareUrl} className="text-xs tabular font-mono" data-testid="share-url" onClick={(e) => e.target.select()} />
                  <Button size="icon" variant="outline" onClick={copyLink} data-testid="share-copy">
                    {copied ? <CheckCircle2 className="w-4 h-4 text-[hsl(var(--success))]" /> : <Copy className="w-4 h-4" />}
                  </Button>
                </div>
                <a href={shareUrl} target="_blank" rel="noopener noreferrer" className="text-xs gold hover:brightness-110">Open in new tab →</a>
              </div>
            )}
          </TabsContent>

          <TabsContent value="email" className="pt-4 space-y-3">
            {!sent ? (
              <>
                <p className="text-sm text-muted-foreground">
                  Sends a pre-written order request with the {lowStockCount} low-stock item{lowStockCount === 1 ? "" : "s"} attached. The supplier can reply straight back to you.
                </p>
                <div>
                  <Label>Supplier email</Label>
                  <Input
                    type="email"
                    value={supplierEmail}
                    onChange={(e) => setSupplierEmail(e.target.value)}
                    placeholder="orders@yoursupplier.com"
                    data-testid="share-supplier-email"
                  />
                </div>
                <div>
                  <Label>Supplier name <span className="text-muted-foreground/60">(optional)</span></Label>
                  <Input
                    value={supplierName}
                    onChange={(e) => setSupplierName(e.target.value)}
                    placeholder="e.g. Iberia Foods"
                    data-testid="share-supplier-name"
                  />
                </div>
                <div>
                  <Label>Notes <span className="text-muted-foreground/60">(optional)</span></Label>
                  <Textarea
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    placeholder="e.g. Need delivery before Friday service. Substitute wild salmon for farmed if unavailable."
                    className="min-h-[64px]"
                    data-testid="share-supplier-notes"
                  />
                </div>
                <Button
                  onClick={emailSupplier}
                  disabled={sending || lowStockCount === 0}
                  className="w-full gap-2"
                  data-testid="share-send-email"
                >
                  {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Mail className="w-4 h-4" />}
                  {lowStockCount === 0 ? "Nothing to order: no low stock" : `Send order request (${lowStockCount} items)`}
                </Button>
              </>
            ) : (
              <div className="rounded-lg border border-[hsl(var(--success))]/40 bg-[hsl(var(--success))]/10 p-4 text-center space-y-2" data-testid="share-supplier-sent">
                <CheckCircle2 className="w-8 h-8 mx-auto text-[hsl(var(--success))]" />
                <div className="font-display text-lg">Order sent.</div>
                <div className="text-xs text-muted-foreground">The supplier will reply straight to your inbox.</div>
              </div>
            )}
          </TabsContent>
        </Tabs>

        <DialogFooter className="pt-2">
          <Button variant="ghost" onClick={close}>Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
